import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
    getUserData, getCatalogData, getProductsBatch, getImage,
    clearFilters, toggleCartOpen, setSelectedProduct, restoreCart
} from '../store/slices/appSlice';
import CartDrawer from '../components/CartDrawer';
import ProductDetailModal from '../components/ProductDetailModal';
import AdvancedSearchFilters from '../components/AdvancedSearchFilters';
import CatalogAboutModal from '../components/CatalogAboutModal';
import AddToCartButton from '../components/AddToCartButton';
import VendorFooter from '../components/VendorFooter';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import { getCurrencySymbol } from '../helper/currencyHelper';
import { getProductCoverUrl } from '../helper/imageHelper';

export const getCoverImageUrl = (p) => {
    return getProductCoverUrl(p);
};

export const getCatalogCoverUrl = (cat) => {
    if (!cat) return null;
    if (cat.onlinePath && typeof cat.onlinePath === 'string' && cat.onlinePath.startsWith('http')) return cat.onlinePath;
    if (cat.coverUrl && typeof cat.coverUrl === 'string' && cat.coverUrl.startsWith('http')) return cat.coverUrl;
    if (cat.coverPath && typeof cat.coverPath === 'string' && cat.coverPath.startsWith('http')) return cat.coverPath;
    return null;
};

export const hexToHsl = (hex) => {
    if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return null;
    let c = hex.substring(1);
    if (c.length === 3) c = c.split('').map(x => x + x).join('');
    if (c.length !== 6) return null;

    const r = parseInt(c.substring(0, 2), 16) / 255;
    const g = parseInt(c.substring(2, 4), 16) / 255;
    const b = parseInt(c.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0, l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
            default: break;
        }
        h /= 6;
    }

    return {
        h: Math.round(h * 360),
        s: `${Math.round(s * 100)}%`,
        l: `${Math.round(l * 100)}%`
    };
};

const CatalogViewPage = () => {
    const { t } = useTranslation();
    const { userID, catalogID } = useParams();
    const dispatch = useDispatch();

    const user = useSelector((state) => state.app.user);
    const catalog = useSelector((state) => state.app.catalog);
    const productsState = useSelector((state) => state.app.products);
    const cart = useSelector((state) => state.app.cart);
    const selectedProductId = useSelector((state) => state.app.gui.selectedProductId);
    const isCartOpen = useSelector((state) => state.app.gui.isCartOpen);
    const filters = useSelector((state) => state.app.filters);

    const isFilterActive = filters.search !== '' || filters.stock !== 'all' || filters.priceRange !== null || filters.onlyDiscounted || filters.onlyNonDiscounted;

    useEffect(() => {
        if (isFilterActive && productsState.hasMore && !productsState.isLoading) {
            dispatch(getProductsBatch({ 
                userID, 
                catalogID, 
                size: 1000, 
                lastRef: productsState.lastProductRef 
            }));
        }
    }, [isFilterActive, productsState.hasMore, productsState.isLoading, productsState.lastProductRef, dispatch, userID, catalogID]);

    const [showAboutModal, setShowAboutModal] = useState(false);
    const [showScrollToTop, setShowScrollToTop] = useState(false);
    const [gridColumns, setGridColumns] = useState(() => {
        const saved = localStorage.getItem('catalogGridColumns');
        return saved ? parseInt(saved, 10) : 1;
    });

    useEffect(() => {
        localStorage.setItem('catalogGridColumns', gridColumns.toString());
    }, [gridColumns]);

    useEffect(() => {
        const handleScroll = () => {
            setShowScrollToTop(window.scrollY > 400);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (userID && catalogID) {
            dispatch(getUserData(userID));
            dispatch(getCatalogData({ userID, catalogID }));
            dispatch(getProductsBatch({ userID, catalogID, size: 24 }));
            dispatch(restoreCart(catalogID));
        }
    }, [dispatch, userID, catalogID]);

    // Ensure images are fetched for any products with picturesRef that haven't loaded yet
    useEffect(() => {
        if (userID && catalogID && productsState.ids.length > 0) {
            productsState.ids.forEach(id => {
                const p = productsState.entities[id];
                if (p && p.picturesRef && p.picturesRef.length > 0) {
                    const firstImageID = p.picturesRef[0];
                    if (!p.pictures || !p.pictures[firstImageID]) {
                        dispatch(getImage({ userID, catalogID, productID: p.id, imageID: firstImageID }));
                    }
                }
            });
        }
    }, [dispatch, userID, catalogID, productsState.ids, productsState.entities]);

    // Dynamic Theme Color Palette from Catalog's color field on :root
    useEffect(() => {
        const catalogColor = catalog.value?.color;
        const hsl = hexToHsl(catalogColor);

        if (hsl) {
            document.documentElement.style.setProperty('--main-h', `${hsl.h}`);
            document.documentElement.style.setProperty('--main-s', `${hsl.s}`);
            document.documentElement.style.setProperty('--main-l', `${hsl.l}`);
        }

        return () => {
            // Restore default brand color on unmount
            document.documentElement.style.setProperty('--main-h', '200');
            document.documentElement.style.setProperty('--main-s', '100%');
            document.documentElement.style.setProperty('--main-l', '24%');
        };
    }, [catalog.value?.color]);

    const observer = useRef();
    const lastProductElementRef = useCallback(node => {
        if (productsState.isLoading) return;
        if (observer.current) observer.current.disconnect();
        
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && productsState.hasMore) {
                dispatch(getProductsBatch({ 
                    userID, 
                    catalogID, 
                    size: 24, 
                    lastRef: productsState.lastProductRef 
                }));
            }
        }, { threshold: 0.1 });
        
        if (node) observer.current.observe(node);
    }, [productsState.isLoading, productsState.hasMore, productsState.lastProductRef, dispatch, userID, catalogID]);

    const cartItemCount = Object.values(cart || {}).reduce((acc, qty) => acc + (typeof qty === 'number' ? qty : 0), 0);

    const productIdsToDisplay = productsState.filteredIds !== null 
        ? productsState.filteredIds 
        : productsState.ids;

    const calculatePrice = (price, discount) => {
        if (!price) return 0;
        if (!discount || discount <= 0) return price;
        return price - (price * (discount / 100));
    };

    const selectedProduct = selectedProductId ? productsState.entities[selectedProductId] : null;
    const catalogCoverUrl = getCatalogCoverUrl(catalog.value);

    return (
        <div className="catalog-view-page bg-gray-50 min-h-100vh">
            {/* Header / Navbar */}
            <header className="header-two bg-white border-bottom shadow-sm sticky-top">
                <div className="container container-lg py-16 flex-between align-items-center flex-wrap gap-12">
                    <div 
                        className="d-flex align-items-center gap-16 cursor-pointer group"
                        onClick={() => setShowAboutModal(true)}
                        title={t('store.vendorInfo')}
                    >
                        {catalogCoverUrl || user.value?.logoUrl ? (
                            <img 
                                src={catalogCoverUrl || user.value?.logoUrl} 
                                alt={catalog.value?.name || user.value?.nombre} 
                                className="w-56 h-56 rounded-circle object-fit-cover border shadow-xs transition-2 group-hover-scale" 
                            />
                        ) : (
                            <div className="w-56 h-56 rounded-circle bg-main-600 text-white flex-center text-xl fw-bold border shadow-xs transition-2">
                                {(catalog.value?.name || 'C')[0]}
                            </div>
                        )}
                        <div className="d-flex flex-column align-items-start">
                            <h4 className="mb-0 fw-bold text-gray-900 transition-2 hover-text-main-600">{catalog.value?.name || t('store.loadingCatalog')}</h4>
                            <div className="d-flex flex-column align-items-start mt-4">
                                <span 
                                    className="text-xs text-gray-500"
                                    style={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        maxWidth: '360px',
                                        textOverflow: 'ellipsis'
                                    }}
                                >
                                    {catalog.value?.description || user.value?.nombre || ''}
                                </span>
                                {(catalog.value?.description?.length > 70) && (
                                    <span className="text-main-600 fw-bold text-xs mt-2 hover-text-main-700 transition-2">
                                        {t('store.readMore')}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-12 flex-wrap">
                        <LanguageSwitcher />

                        <button 
                            type="button" 
                            className="btn btn-outline-gray-200 text-gray-700 rounded-circle w-44 h-44 flex-center hover-bg-main-50 hover-text-main-600 hover-border-main-600 transition-2"
                            onClick={() => setShowAboutModal(true)}
                            title={t('store.about')}
                            aria-label={t('store.about')}
                        >
                            <i className="ph ph-info text-xl"></i>
                        </button>

                        <button 
                            type="button" 
                            className={`btn position-relative flex-align gap-8 px-20 py-10 rounded-pill transition-2 d-none d-md-flex ${
                                cartItemCount > 0 
                                    ? 'btn-main shadow-xs' 
                                    : 'btn-outline-gray-200 text-gray-400 bg-gray-50 border-gray-200 hover-bg-gray-100 hover-text-gray-600'
                            }`}
                            onClick={() => dispatch(toggleCartOpen(true))}
                            title={cartItemCount > 0 ? t('store.itemsInCart', { count: cartItemCount }) : t('store.cartEmpty')}
                        >
                            <i className="ph ph-shopping-cart text-xl"></i>
                            <span className="fw-bold d-none d-sm-inline">{t('store.cart')}</span>
                            {cartItemCount > 0 && (
                                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-2 border-white shadow-xs px-8 py-4 text-2xs fw-bold">
                                    {cartItemCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content Area */}
            <div className="container container-lg py-40">
                {/* Advanced Search & Filter Bar */}
                <AdvancedSearchFilters />

                {/* View Controls (Mobile Only) */}
                {productIdsToDisplay.length > 0 && (
                    <div className="d-flex justify-content-end mt-16 mb-16 d-sm-none">
                        <button 
                            className="btn btn-outline-gray-300 text-gray-700 bg-white d-flex align-items-center gap-8 py-6 px-12 rounded-8 transition-2"
                            onClick={() => setGridColumns(prev => prev === 1 ? 2 : 1)}
                            title={gridColumns === 1 ? "2 items per row" : "1 item per row"}
                        >
                            <i className={`ph ${gridColumns === 1 ? 'ph-squares-four' : 'ph-list'} text-lg`}></i>
                        </button>
                    </div>
                )}

                {/* Products Grid */}
                {productsState.isLoading && productIdsToDisplay.length === 0 ? (
                    <div className="text-center py-80">
                        <div className="spinner-border text-main-600" role="status">
                            <span className="visually-hidden">{t('store.loadingMore')}</span>
                        </div>
                        <p className="text-gray-500 mt-16">{t('store.loadingProducts')}</p>
                    </div>
                ) : productIdsToDisplay.length === 0 ? (
                    <div className="text-center py-80 bg-white rounded-16">
                        <i className="ph ph-package text-6xl text-gray-300 mb-16 d-block"></i>
                        <h5>{t('store.noProductsFound')}</h5>
                        <p className="text-gray-500">{t('store.noProductsDesc')}</p>
                        <button className="btn btn-main rounded-pill px-24 py-10 mt-12" onClick={() => dispatch(clearFilters())}>
                            {t('store.clearFilters')}
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="row g-4">
                            {productIdsToDisplay.map((id, index) => {
                                const p = productsState.entities[id];
                                if (!p) return null;
                                const finalPrice = calculatePrice(p.price, p.discount);
                                const coverImg = getCoverImageUrl(p);
                                const isLastProduct = productIdsToDisplay.length === index + 1;
                                const mobileColClass = gridColumns === 1 ? "col-12" : "col-6";

                                const stockConfig = {
                                    available: { label: t('search.available'), color: 'success' },
                                    ondemand: { label: t('search.onDemand'), color: 'info' },
                                    ask: { label: t('search.ask'), color: 'warning' },
                                    transit: { label: t('search.inTransit'), color: 'primary' },
                                    soldout: { label: t('search.soldOut'), color: 'danger' }
                                }[p.stock || 'available'] || { label: t('search.available'), color: 'success' };

                                return (
                                    <div 
                                        key={id} 
                                        className={`${mobileColClass} col-sm-6 col-md-4 col-lg-3`}
                                        ref={isLastProduct ? lastProductElementRef : null}
                                    >
                                        <div className="product-card h-100 bg-white rounded-16 border hover-border-main-600 transition-2 position-relative overflow-hidden d-flex flex-column">
                                            {p.discount > 0 && (
                                                <span className="position-absolute top-0 start-0 bg-danger text-white text-xs fw-bold px-10 py-4 rounded-end-pill z-1 mt-12">
                                                    -{p.discount}%
                                                </span>
                                            )}
                                            
                                            <div 
                                                className="product-card__thumb flex-center bg-gray-50 p-0 position-relative cursor-pointer overflow-hidden" 
                                                style={{ height: '240px' }}
                                                onClick={() => dispatch(setSelectedProduct(id))}
                                            >
                                                <img 
                                                    src={coverImg} 
                                                    alt={p.name} 
                                                    className="mw-100 mh-100 w-auto h-auto transition-2 hover-scale" 
                                                    style={{ mixBlendMode: 'multiply' }}
                                                />
                                                <span className={`position-absolute bottom-0 end-0 m-8 z-1 badge bg-${stockConfig.color}-50 text-${stockConfig.color}-600 border border-${stockConfig.color}-200 rounded-pill px-8 py-2 text-2xs fw-bold`}>
                                                    {stockConfig.label}
                                                </span>
                                            </div>

                                            <div className="product-card__content p-16 flex-grow-1 d-flex flex-column justify-content-between">
                                                <div>
                                                    {p.code && (
                                                        <span className="text-xs text-main-600 fw-semibold text-uppercase d-block mb-4">{p.code}</span>
                                                    )}
                                                    <h6 
                                                        className="text-md fw-bold text-gray-900 mt-4 mb-8 text-line-2 cursor-pointer hover-text-main-600"
                                                        onClick={() => dispatch(setSelectedProduct(id))}
                                                    >
                                                        {p.name}
                                                    </h6>
                                                </div>

                                                <div>
                                                    <div className="d-flex align-items-baseline gap-8 mb-12">
                                                        <span className="text-lg fw-bold text-main-600">
                                                            {getCurrencySymbol(catalog.value)}{finalPrice.toFixed(2)}
                                                        </span>
                                                        {p.discount > 0 && (
                                                            <span className="text-xs text-gray-400 text-decoration-line-through">
                                                                {getCurrencySymbol(catalog.value)}{p.price?.toFixed(2)}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <AddToCartButton productId={id} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {productsState.hasMore && productsState.isLoading && (
                            <div className="text-center mt-40">
                                <div className="spinner-border text-main-600" role="status">
                                    <span className="visually-hidden">{t('store.loadingMore')}</span>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Product Detail Modal */}
            {selectedProduct && <ProductDetailModal product={selectedProduct} />}

            {/* Mobile Floating Cart Button */}
            <div className={`position-fixed bottom-0 start-0 mb-32 z-3 d-md-none ${isCartOpen ? 'd-none' : ''}`}>
                <button
                    type="button"
                    className={`btn w-48 h-56 flex-center shadow-lg transition-2 position-relative ${
                        cartItemCount > 0 ? 'btn-main' : 'bg-white text-gray-700 border border-gray-200 border-start-0'
                    }`}
                    style={{ borderRadius: '0 28px 28px 0' }}
                    onClick={() => dispatch(toggleCartOpen(true))}
                    title={t('store.cart')}
                >
                    <i className="ph ph-shopping-cart text-2xl"></i>
                    {cartItemCount > 0 && (
                        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-2 border-white px-8 py-4 text-xs fw-bold">
                            {cartItemCount}
                        </span>
                    )}
                </button>
            </div>

            {/* Bottom Right Action Buttons */}
            <div className="position-fixed bottom-0 end-0 mb-32 me-16 m-md-24 z-3 d-flex flex-column gap-12 align-items-center">
                {/* Desktop Floating Cart Button */}
                <button
                    type="button"
                    className={`btn rounded-circle w-48 h-48 flex-center shadow-lg transition-2 position-relative d-none d-md-flex ${
                        cartItemCount > 0 ? 'btn-main' : 'bg-white text-gray-700 border border-gray-200'
                    }`}
                    style={{ 
                        opacity: showScrollToTop ? '1' : '0', 
                        visibility: showScrollToTop ? 'visible' : 'hidden',
                        transform: showScrollToTop ? 'translateY(0)' : 'translateY(20px)' 
                    }}
                    onClick={() => dispatch(toggleCartOpen(true))}
                >
                    <i className="ph ph-shopping-cart text-lg"></i>
                    {cartItemCount > 0 && (
                        <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger border border-2 border-white px-8 py-4 text-xs fw-bold">
                            {cartItemCount}
                        </span>
                    )}
                </button>

                {/* Scroll To Top Button (All Devices) */}
                <button
                    type="button"
                    className="btn btn-main rounded-circle w-44 h-44 flex-center shadow-md transition-2 d-flex"
                    style={{ 
                        opacity: showScrollToTop ? '1' : '0', 
                        visibility: showScrollToTop ? 'visible' : 'hidden',
                        transform: showScrollToTop ? 'translateY(0)' : 'translateY(20px)' 
                    }}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                    <i className="ph ph-arrow-up text-lg"></i>
                </button>
            </div>

            {/* Cart Drawer */}
            <CartDrawer />

            {/* Catalog About Modal */}
            <CatalogAboutModal isOpen={showAboutModal} onClose={() => setShowAboutModal(false)} />
            <VendorFooter onOpenAbout={() => setShowAboutModal(true)} />
        </div>
    );
};

export default CatalogViewPage;
